package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.MultiColumnVirtualList
import top.wcpe.allincore.ui.runtime.element.MultiColumnVirtualList.ColumnAlign
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.math.roundToInt

/**
 * MultiColumnVirtualList 表头渲染器：在通用 [ScrollableContainerRenderer] 基础上叠加表头绘制层。
 *
 * 历史回归：MultiColumnVirtualList 直接使用通用 `ScrollableContainerRenderer`，只画背景 + 滚动条，
 * **完全没渲染表头文字**——columnDefs / headerHeight / headerSticky 在元素层都已实现，
 * 但渲染层从未把 ColumnDef.title 写到画面上。
 *
 * 本渲染器在 [renderBackground] 内紧接父类背景之后画一层表头：表头底色用主题 surfaceVariant
 * （若 DSL 显式背景透明，自动覆盖；若不透明，叠在上面），表头文字按 [ColumnAlign] 对齐到该列
 * 水平起 / 中 / 末位置，垂直居中到表头高度。sticky 时表头不随 scrollY 滚动。
 */
@Suppress("MagicNumber")
class MultiColumnVirtualListRenderer : ScrollableContainerRenderer() {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val sc = element as ScrollableContainer<*>
        renderBackground(sc, box, bridge)
        renderScrollbar(sc, box, bridge)
    }

    internal override fun renderBackground(sc: ScrollableContainer<*>, box: Box, bridge: UiRenderBridge) {
        super.renderBackground(sc, box, bridge)
        if (sc is MultiColumnVirtualList) {
            renderHeader(sc, box, bridge)
        }
    }

    /** 绘制表头：列起始位置 + 标题文本 + 表头底分隔线。 */
    private fun renderHeader(table: MultiColumnVirtualList, box: Box, bridge: UiRenderBridge) {
        val headerH = table.headerHeight()
        if (headerH <= 0f) {
            return
        }
        val defs = table.columnDefs()
        if (defs.isEmpty()) {
            return
        }
        val theme = ThemeAccess.current()

        // 表头底色：主题 surfaceVariant，半透明叠加，既能让用户看到表头区与行区分，又不抢 DSL 自定义 background。
        val headerBg = topAlpha(theme.surfaceVariant, 0.6f)
        if ((headerBg ushr 24) != 0) {
            bridge.fillRect(box.x(), box.y(), box.width(), headerH, headerBg)
        }

        // 列水平排布：从 box.x() 起依次累加 width，扣减 scrollX 让水平滚动也能影响表头位置。
        val textColor = theme.onSurface
        var cursor = box.x() - table.scrollX()
        val textY = box.y() + (headerH - HEADER_TEXT_SCALE * GLYPH_HEIGHT) / 2f
        for (def in defs) {
            val title = def.title
            if (!title.isNullOrEmpty()) {
                val textX = computeTextX(cursor, def.width, title, def.align, bridge)
                bridge.drawText(title, textX, textY, textColor, HEADER_TEXT_SCALE)
            }
            cursor += def.width
        }

        // 表头底分隔线：用 outlineVariant 主题色，把表头与行区视觉分割。
        bridge.fillRect(box.x(), box.y() + headerH - HEADER_DIVIDER, box.width(), HEADER_DIVIDER, theme.outlineVariant)
    }

    private companion object {
        /** 表头文字缩放比例。 */
        private const val HEADER_TEXT_SCALE = 0.7f

        /** 表头分隔线粗细（像素）。 */
        private const val HEADER_DIVIDER = 1f

        /** 表头文字相对列起 / 末的留白。 */
        private const val HEADER_TEXT_PAD = 4f

        /** 一行字符的大致像素高（用于文字垂直居中）。 */
        private const val GLYPH_HEIGHT = 8f

        /** 按 align 计算列内文字 X：START 起 + pad、CENTER 居中、END 末 - 文本宽 - pad。 */
        private fun computeTextX(
            colStart: Float,
            colWidth: Float,
            text: String,
            align: ColumnAlign,
            bridge: UiRenderBridge,
        ): Float = when (align) {
            ColumnAlign.CENTER -> colStart + (colWidth - bridge.textWidth(text, HEADER_TEXT_SCALE)) / 2f
            ColumnAlign.END -> colStart + colWidth - bridge.textWidth(text, HEADER_TEXT_SCALE) - HEADER_TEXT_PAD
            else -> colStart + HEADER_TEXT_PAD
        }

        /** 把目标颜色的 alpha 通道改为 ratio 比例（0..1）。 */
        private fun topAlpha(argb: Int, ratio: Float): Int {
            val a = (((argb ushr 24) and 0xFF) * ratio.coerceIn(0f, 1f)).roundToInt()
            return (a shl 24) or (argb and 0x00FFFFFF)
        }
    }
}
